import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { Plus, Loader2, Inbox } from "lucide-react";
import AuthContext from "../context/AuthContext";
import { fetchOccurrences, fetchEventInvites } from "../api/EventAPI";
import { mapOccurrenceToCalendarEvent } from "../utils/fullCalendarMapping";
import { isShownOnCalendar } from "../utils/eventDomain";
import { applyChangeToInvites, applyChangeToOccurrences, useEventChanges } from "../utils/eventChanges";
import { pendingInviteCount, withoutInvite, withoutSeriesInvites } from "../utils/eventInvites";
import { useModal } from "../components/ModalManager";
import { useWindowSize } from "../hooks/useWindowSize";

export default function CalendarPage() {
  const { token } = useContext(AuthContext);
  const { openModal } = useModal();
  const { width } = useWindowSize();
  const isMobile = width < 640;

  // Occurrences overlapping the visible range — what the calendar actually
  // renders, canceled ones included (shown struck through). Full Event
  // details are loaded by EventFocusModal on click, not held here.
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");

  // Event invite inbox — fetched independently of the calendar's visible
  // date range so the unanswered badge is accurate as soon as the page
  // loads, not only once the inbox modal is opened. Series and occurrence
  // invitations, identified by (scope, id) — see utils/eventInvites.
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState("");

  // Tracks the most recently requested range so a slow/out-of-order response
  // (e.g. rapid month navigation) can't overwrite newer data. Also lets
  // create/edit/remove refetch whatever range is currently visible.
  const rangeRef = useRef(null);
  const requestSeqRef = useRef(0);

  const loadOccurrences = useCallback(
    async (start, end) => {
      if (!token) return;

      rangeRef.current = { start, end };
      const requestId = ++requestSeqRef.current;
      const isLatest = () => requestSeqRef.current === requestId;

      setLoading(true);
      setError("");
      try {
        const data = await fetchOccurrences(token, { start, end, includeCanceled: true });
        if (isLatest()) {
          setOccurrences(data);
        }
      } catch (err) {
        console.error("Error loading events", err);
        if (isLatest()) {
          setError("Couldn't load your events. Please try again.");
        }
      } finally {
        if (isLatest()) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    },
    [token]
  );

  // Fires on initial mount and whenever the visible range changes
  // (prev/next/today navigation or switching Month/Week/Day). This is the
  // only renderer-specific input: it just hands a start/end range to
  // loadOccurrences, which any other calendar renderer could call the same way.
  const handleDatesSet = useCallback(
    (arg) => {
      // Real Date objects → unambiguous UTC ISO strings for the backend.
      loadOccurrences(arg.start.toISOString(), arg.end.toISOString());
    },
    [loadOccurrences]
  );

  // Creating or editing an event can add/regenerate occurrences server-side
  // (occurrence ids may change), so re-read the visible range rather than
  // patching occurrences locally.
  const reloadVisibleRange = useCallback(() => {
    if (rangeRef.current) loadOccurrences(rangeRef.current.start, rangeRef.current.end);
  }, [loadOccurrences]);

  // Every Event mutation — from this page, Home, the invite inbox or a
  // notification — is announced (eventChanges); re-read the visible range
  // from the backend (one request; requestSeqRef drops stale ones, including
  // one already in flight). A confirmed occurrence result is applied first,
  // so the grid is right even if the re-read fails: an occurrence the viewer
  // removed (or can no longer see) is dropped, its pending invitation with
  // it; a deleted Event's invitations go too rather than waiting for the next
  // GET /events/invites/.
  useEventChanges((change) => {
    setOccurrences((prev) => applyChangeToOccurrences(prev, change));
    setInvites((prev) => applyChangeToInvites(prev, change));
    reloadVisibleRange();
  });

  // Loads the invite inbox once on mount (independent of the calendar's
  // visible date range).
  useEffect(() => {
    if (!token) return;
    let canceled = false;

    (async () => {
      setInvitesLoading(true);
      setInvitesError("");
      try {
        const data = await fetchEventInvites(token, { includeOccurrences: true });
        if (!canceled) setInvites(data);
      } catch (err) {
        console.error("Error loading event invites", err);
        if (!canceled) setInvitesError("Couldn't load your invitations. Please try again.");
      } finally {
        if (!canceled) setInvitesLoading(false);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [token]);

  // GET /events/invites/ only ever returns unanswered invites, so an
  // answered one is removed from the inbox rather than shown with a status
  // label — mirrors that backend semantics exactly. Accepting puts the
  // event on the calendar; that refresh arrives through useEventChanges.
  // Answering a series invitation (here or in the Event's detail) resolves
  // series invitations to that Event only; its occurrence invitations stay.
  const handleInviteAnswered = ({ invite, eventId }) => {
    setInvites((prev) =>
      invite ? withoutInvite(prev, invite) : withoutSeriesInvites(prev, eventId)
    );
  };

  // Both scopes, each invitation once.
  const unansweredCount = pendingInviteCount(invites);

  // Pending invitations and declines stay off the grid, except a date a
  // series participant declined on its own: shown subdued (as on iOS).
  const visibleOccurrences = useMemo(() => occurrences.filter(isShownOnCalendar), [occurrences]);

  // Memoized so unrelated state changes (invites, loading) don't hand
  // FullCalendar a new events array to diff.
  const calendarEvents = useMemo(
    () => visibleOccurrences.map(mapOccurrenceToCalendarEvent),
    [visibleOccurrences]
  );

  // Opens the shared Event focus view for the clicked occurrence (canceled
  // ones open read-only). Refreshing after changes goes through
  // useEventChanges above.
  const handleEventClick = (info) => {
    const { eventId, occurrence } = info.event.extendedProps;
    openModal("EventFocusModal", { eventId, occurrence });
  };

  // Canceled occurrences (and dates declined on their own) are struck
  // through; say which to assistive tech too.
  const handleEventDidMount = (info) => {
    const { isCanceled, isDeclined } = info.event.extendedProps;
    const prefix = isCanceled ? "Canceled" : isDeclined ? "Declined" : null;
    if (prefix) {
      info.el.setAttribute("aria-label", `${prefix}: ${info.event.title}`);
      info.el.setAttribute("title", `${prefix}: ${info.event.title}`);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Calendar</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              openModal("EventInviteInbox", {
                invites,
                loading: invitesLoading,
                error: invitesError,
                onInviteAnswered: handleInviteAnswered,
              })
            }
            aria-label="Event invitations"
            className="relative flex items-center rounded-full border border-gray-300 p-2 hover:bg-gray-100"
          >
            <Inbox size={18} />
            {unansweredCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-purple-600 px-1 text-[10px] font-semibold text-white">
                {unansweredCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => openModal("EventCreateForm", { onSuccess: reloadVisibleRange })}
            className="flex items-center gap-1.5 rounded-full bg-black text-white px-3 py-1.5 text-sm hover:bg-gray-800"
          >
            <Plus size={16} />
            Create Event
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {hasLoadedOnce && !loading && !error && visibleOccurrences.length === 0 && (
        <p className="text-sm text-gray-500 mb-3">No events yet.</p>
      )}

      <div className="relative">
        {loading && (
          <div className="absolute top-2 right-2 z-10 text-gray-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}

        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: isMobile ? "dayGridMonth,dayGridDay" : "dayGridMonth,dayGridWeek,dayGridDay",
          }}
          height="auto"
          events={calendarEvents}
          eventClick={handleEventClick}
          eventDidMount={handleEventDidMount}
          datesSet={handleDatesSet}
          dayMaxEvents={isMobile ? 2 : true}
        />
      </div>
    </div>
  );
}
